import { useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  ArrowRight,
  Loader2,
  AlertCircle,
  RotateCw,
  MailCheck,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;
if (!API_URL) {
  console.error("REACT_APP_BACKEND_URL is not defined in frontend/.env");
}

function Otp() {
  const navigate = useNavigate();

  const location = useLocation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const inputs = useRef([]);

  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setInterval(() => setSeconds((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [seconds]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError("");
    if (value && index < 5) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) inputs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(pasted)) return;
    setOtp(pasted.split(""));
    inputs.current[5]?.focus();
  };

  const verifyOTP = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }
    try {
      setLoading(true);
setError("");
setSuccess("");
      const userId =
  location.state?.type === "forgot-password"
    ? localStorage.getItem("resetUserId")
    : localStorage.getItem("userId");
    if (!userId) {
  setError("Session expired. Please login again.");
  return;
}
      const res = await axios.post(`${API_URL}/api/auth/verify-otp`, {
  userId,
  otp: code,
});

if (res.data.success) {
    if (res.data.token) {
        localStorage.setItem("token", res.data.token);
    }
    if (res.data.user?.full_name) {
        localStorage.setItem("userName", res.data.user.full_name);
    }
    if (res.data.user?.email) {
        localStorage.setItem("email", res.data.user.email);
    }

    setSuccess("OTP Verified Successfully");

    setTimeout(() => {

        if (location.state?.type === "forgot-password") {

            navigate("/reset-password");

        } else {

            navigate("/dashboard");

        }

    }, 700);

}
 else {
        setError(res.data.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    try {
      setResending(true);
      setError("");
      const userId =
  location.state?.type === "forgot-password"
    ? localStorage.getItem("resetUserId")
    : localStorage.getItem("userId");
    if (!userId) {
  setError("Session expired. Please login again.");
  return;
}
      await axios.post(`${API_URL}/api/auth/resend-otp`, { userId });
      setSuccess("A fresh OTP has been sent");
      setOtp(["", "", "", "", "", ""]);
inputs.current[0]?.focus();
      setSeconds(30);
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Unable to resend OTP. Please try again."
      );
    } finally {
      setResending(false);
    }
  };

  const otpComplete = otp.every((d) => d !== "");

  return (
    <div className="min-h-[100dvh] w-full bg-slate-50 relative overflow-hidden flex items-center justify-center px-5 py-10">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px);} to{opacity:1;transform:translateY(0);} }
        @keyframes ping2  { 0%{transform:scale(1);opacity:.5;} 100%{transform:scale(1.8);opacity:0;} }
        .anim-fade-up{animation:fadeUp .6s cubic-bezier(.22,1,.36,1) both;}
        .anim-ping{animation:ping2 1.8s cubic-bezier(0,0,.2,1) infinite;}
        .delay-1{animation-delay:.05s;} .delay-2{animation-delay:.15s;}
        .delay-3{animation-delay:.25s;} .delay-4{animation-delay:.35s;}
        .delay-5{animation-delay:.45s;} .delay-6{animation-delay:.55s;}
      `}</style>

      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-sky-100/70 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="anim-fade-up delay-2 rounded-2xl bg-white border border-slate-200/70 shadow-xl shadow-slate-200/60 p-7 sm:p-9">
          {/* Shield icon with pulse ring */}
          <div className="anim-fade-up delay-3 mx-auto mb-5 w-fit relative">
            <span className="absolute inset-0 rounded-2xl bg-blue-400/40 anim-ping" />
            <div className="relative h-14 w-14 rounded-2xl overflow-hidden shadow-md shadow-blue-500/25">
              <img src={logo} alt="ZA Optical" className="h-full w-full object-cover" />
            </div>
          </div>

          <div className="text-center">
            <h2 className="anim-fade-up delay-3 text-[24px] sm:text-[28px] font-semibold tracking-tight text-slate-900">
              Verify OTP
            </h2>
            <p className="anim-fade-up delay-4 mt-2 text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">
              Enter the 6-digit code we sent to your registered email.
            </p>
          </div>

          {/* OTP inputs */}
          <div
            className="anim-fade-up delay-5 mt-7 flex justify-between gap-2 sm:gap-2.5"
            onPaste={handlePaste}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputs.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                aria-label={`Digit ${index + 1}`}
                className={`h-14 w-full min-w-0 sm:h-16 rounded-xl border bg-white text-center text-2xl sm:text-3xl font-semibold text-slate-900 outline-none transition
                  ${
                    error
                      ? "border-red-400 ring-2 ring-red-100"
                      : digit
                      ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50/40"
                      : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  }`}
              />
            ))}
          </div>

          {error && (
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-red-600">
              <AlertCircle size={12} /> {error}
            </p>
          )}
          {success && !error && (
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-emerald-600">
              <MailCheck size={12} /> {success}
            </p>
          )}

          <button
            onClick={verifyOTP}
            data-testid="otp-verify-btn"
            disabled={loading || !otpComplete}
            className="anim-fade-up delay-6 group mt-6 w-full rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 py-3.5 font-medium text-white shadow-md shadow-blue-500/20 transition-all
                      active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]
                      flex items-center justify-center gap-2 text-[15px]"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Verifying…
              </>
            ) : (
              <>
                Verify OTP
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </>
            )}
          </button>

          {/* Resend */}
          <div className="mt-5 text-center text-sm">
            {seconds > 0 ? (
              <p className="text-slate-500">
                Didn't receive the code?{" "}
                <span className="text-blue-600 font-medium tabular-nums">
                  Resend in {seconds}s
                </span>
              </p>
            ) : (
              <button
                onClick={resendOTP}
                disabled={resending}
                className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium transition disabled:opacity-60"
              >
                <RotateCw size={14} className={resending ? "animate-spin" : ""} />
                {resending ? "Sending…" : "Resend OTP"}
              </button>
            )}
          </div>

          <div className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
            <ShieldCheck size={13} className="text-emerald-500" />
            Secured with 256-bit encryption
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400 tracking-wide">
          © 2026 ZA Optical
        </p>
      </div>
    </div>
  );
}

export default Otp;